/**
 * @module ui/components/error-recovery
 * @description Enhanced error handling with recovery options
 * Provides context-aware error messages with actionable recovery steps
 */

import { getElement } from '../dom/dom-manager.js';
import { logger } from '../../utils/logger.js';
import { ENTITY_TYPES } from '../../utils/constants.js';

/**
 * Error context types for specialized handling
 */
export const ERROR_CONTEXTS = {
    ENTITY_NOT_FOUND: 'entity-not-found',
    API_FAILURE: 'api-failure',
    NETWORK_ERROR: 'network-error',
    OFFLINE: 'offline',
    FILE_OPERATION: 'file-operation-error',
    AUTHENTICATION: 'authentication-error',
    PERMISSION_DENIED: 'permission-denied',
    UNKNOWN: 'unknown'
};

/**
 * Network connectivity state
 */
let isOnline = navigator.onLine;
let connectivityListenersAttached = false;

/**
 * Show enhanced error message with recovery options
 * @param {Error} error - The error object
 * @param {Object} context - Error context information
 * @param {string} context.type - Error context type from ERROR_CONTEXTS
 * @param {string} [context.entityType] - Entity type (projects, opportunities, etc.)
 * @param {string} [context.entityId] - Entity ID that failed
 * @param {string} [context.entityName] - Entity name
 * @param {string} [context.searchTerm] - Search term used
 * @param {string} [context.fileName] - File name for file operations
 * @param {number} [context.statusCode] - HTTP status code
 * @param {Function} [context.retryAction] - Function to call for retry
 */
export function showErrorWithRecovery(error, context = {}) {
    logger.error('Error with context:', { error, context });

    // Hide loading states
    const loadingSection = getElement('loadingSection');
    if (loadingSection) {
        loadingSection.classList.add('hidden');
    }

    const errorContainer = getOrCreateErrorContainer();

    // Build error content based on context
    const errorContent = buildErrorContent(error, context);

    errorContainer.innerHTML = errorContent;
    errorContainer.classList.remove('hidden');

    // Hide other sections when showing critical errors
    if (context.type !== ERROR_CONTEXTS.FILE_OPERATION) {
        const filesSection = getElement('filesSection');
        if (filesSection) {
            filesSection.classList.add('hidden');
        }
    }

    // Attach event listeners to recovery buttons
    attachRecoveryListeners(errorContainer, context);

    // Auto-dismiss non-critical errors after 10 seconds
    if (context.type === ERROR_CONTEXTS.FILE_OPERATION) {
        setTimeout(() => {
            hideError();
        }, 10000);
    }
}

/**
 * Build error content HTML based on context
 * @private
 */
function buildErrorContent(error, context) {
    const { type, entityType, entityId, entityName, fileName, statusCode, retryAction } = context;

    let errorTitle = 'Something went wrong';
    let errorMessage = error.message || 'An unexpected error occurred';
    let errorIcon = '⚠️';
    let recoveryActions = '';

    switch (type) {
        case ERROR_CONTEXTS.ENTITY_NOT_FOUND:
            errorTitle = `${getEntityLabel(entityType)} Not Found`;
            errorIcon = '🔍';
            errorMessage = entityId
                ? `Could not find ${getEntityLabel(entityType).toLowerCase()} with ID: ${escapeHtml(entityId)}`
                : `The ${getEntityLabel(entityType).toLowerCase()} you're looking for doesn't exist or has been removed.`;
            recoveryActions = `
                <button class="btn btn-primary" data-action="search">
                    🔍 Search for ${getEntityLabel(entityType)}
                </button>
                <button class="btn btn-secondary" data-action="home">
                    🏠 Return to Home
                </button>
                ${entityName ? `
                    <button class="btn btn-secondary" data-action="search-by-name">
                        🔎 Search for "${escapeHtml(entityName)}"
                    </button>
                ` : ''}
            `;
            break;

        case ERROR_CONTEXTS.API_FAILURE:
            errorTitle = 'Failed to Load Data';
            errorIcon = '❌';
            const statusText = statusCode ? ` (Status: ${statusCode})` : '';
            errorMessage = `Unable to retrieve data from the server${statusText}. ${error.message}`;

            // Check if we might have partial results
            if (context.hasPartialResults) {
                errorMessage += '<br><br>Some data may be displayed below.';
            }

            recoveryActions = `
                ${retryAction ? `
                    <button class="btn btn-primary" data-action="retry">
                        🔄 Try Again
                    </button>
                ` : ''}
                <button class="btn btn-secondary" data-action="home">
                    🏠 Return to Home
                </button>
                <button class="btn btn-secondary" data-action="refresh">
                    ↻ Refresh Page
                </button>
            `;
            break;

        case ERROR_CONTEXTS.NETWORK_ERROR:
            errorTitle = 'Network Connection Problem';
            errorIcon = '📡';
            errorMessage = 'Unable to connect to the server. Please check your internet connection.';
            recoveryActions = `
                ${retryAction ? `
                    <button class="btn btn-primary" data-action="retry">
                        🔄 Retry Connection
                    </button>
                ` : ''}
                <button class="btn btn-secondary" data-action="check-status">
                    📊 Check System Status
                </button>
                <button class="btn btn-secondary" data-action="home">
                    🏠 Return to Home
                </button>
            `;
            break;

        case ERROR_CONTEXTS.OFFLINE:
            errorTitle = 'No Internet Connection';
            errorIcon = '🔌';
            errorMessage = 'You appear to be offline. Please check your internet connection and try again.';
            recoveryActions = `
                <button class="btn btn-primary" data-action="wait-online">
                    ⏳ Waiting for Connection...
                </button>
                <button class="btn btn-secondary" data-action="home">
                    🏠 Return to Home
                </button>
            `;
            break;

        case ERROR_CONTEXTS.FILE_OPERATION:
            errorTitle = 'File Operation Failed';
            errorIcon = '📄';
            errorMessage = fileName
                ? `Failed to process file "${escapeHtml(fileName)}": ${error.message}`
                : `File operation failed: ${error.message}`;
            recoveryActions = `
                ${retryAction ? `
                    <button class="btn btn-primary" data-action="retry">
                        🔄 Try Again
                    </button>
                ` : ''}
                <button class="btn btn-secondary" data-action="dismiss">
                    ✓ Dismiss
                </button>
            `;
            break;

        case ERROR_CONTEXTS.AUTHENTICATION:
            errorTitle = 'Authentication Error';
            errorIcon = '🔒';
            errorMessage = 'Your session may have expired or you don\'t have permission to access this resource.';
            recoveryActions = `
                <button class="btn btn-primary" data-action="sign-in">
                    🔐 Sign In Again
                </button>
                <button class="btn btn-secondary" data-action="home">
                    🏠 Return to Home
                </button>
            `;
            break;

        case ERROR_CONTEXTS.PERMISSION_DENIED:
            errorTitle = 'Access Denied';
            errorIcon = '🚫';
            errorMessage = 'You don\'t have permission to access this resource. Please contact your administrator.';
            recoveryActions = `
                <button class="btn btn-secondary" data-action="home">
                    🏠 Return to Home
                </button>
            `;
            break;

        default:
            errorTitle = 'Unexpected Error';
            errorIcon = '⚠️';
            recoveryActions = `
                ${retryAction ? `
                    <button class="btn btn-primary" data-action="retry">
                        🔄 Try Again
                    </button>
                ` : ''}
                <button class="btn btn-secondary" data-action="home">
                    🏠 Return to Home
                </button>
                <button class="btn btn-secondary" data-action="refresh">
                    ↻ Refresh Page
                </button>
            `;
    }

    // Build the HTML
    return `
        <div class="error-content">
            <div class="error-icon">${errorIcon}</div>
            <h2 class="error-title">${errorTitle}</h2>
            <p class="error-message">${errorMessage}</p>
            
            ${recoveryActions ? `
                <div class="error-actions">
                    ${recoveryActions}
                </div>
            ` : ''}
            
            <details class="error-details">
                <summary>Technical Details</summary>
                <div class="error-technical">
                    <p><strong>Error:</strong> ${escapeHtml(error.name || 'Error')}</p>
                    <p><strong>Message:</strong> ${escapeHtml(error.message)}</p>
                    ${statusCode ? `<p><strong>Status Code:</strong> ${statusCode}</p>` : ''}
                    ${error.stack ? `
                        <pre>${escapeHtml(error.stack.substring(0, 500))}</pre>
                    ` : ''}
                    <p class="error-timestamp"><strong>Time:</strong> ${new Date().toLocaleString()}</p>
                </div>
            </details>
        </div>
    `;
}

/**
 * Attach event listeners to recovery action buttons
 * @private
 */
function attachRecoveryListeners(container, context) {
    const buttons = container.querySelectorAll('[data-action]');

    buttons.forEach(button => {
        button.addEventListener('click', () => {
            const action = button.getAttribute('data-action');
            handleRecoveryAction(action, context);
        });
    });
}

/**
 * Handle recovery action based on button click
 * @private
 */
function handleRecoveryAction(action, context) {
    switch (action) {
        case 'home':
            hideError();
            window.location.href = window.location.pathname;
            break;

        case 'refresh':
            window.location.reload();
            break;

        case 'retry':
            if (context.retryAction && typeof context.retryAction === 'function') {
                hideError();
                context.retryAction();
            }
            break;

        case 'search':
            hideError();
            // Trigger entity type search
            const entityType = context.entityType || 'projects';
            if (window.showEntityTypeSearch) {
                window.showEntityTypeSearch(entityType);
            } else {
                window.location.href = window.location.pathname;
            }
            break;

        case 'search-by-name':
            hideError();
            if (context.entityName && window.performUniversalSearch) {
                const searchInput = document.getElementById('unified-search-input');
                if (searchInput) {
                    searchInput.value = context.entityName;
                    // Trigger input event for search
                    const event = new window.Event('input', { bubbles: true });
                    searchInput.dispatchEvent(event);
                }
            }
            break;

        case 'sign-in':
            hideError();
            if (window.auth) {
                window.location.reload();
            }
            break;

        case 'dismiss':
            hideError();
            break;

        case 'wait-online': {
            const btn = document.querySelector('[data-action="wait-online"]');
            if (btn) {
                btn.disabled = true;
                btn.textContent = '⏳ Waiting for Connection...';
            }
            // The online listener will handle reconnection
            break;
        }

        case 'check-status':
            window.open('https://status.azure.com', '_blank');
            break;
    }
}

/**
 * Get or create the error recovery container
 * @private
 */
function getOrCreateErrorContainer() {
    let container = document.getElementById('error-recovery-container');

    if (!container) {
        container = document.createElement('div');
        container.id = 'error-recovery-container';
        container.className = 'error-recovery-container hidden';

        const main = document.querySelector('main');
        if (main) {
            main.appendChild(container);
        }
    }

    return container;
}

/**
 * Hide error recovery container
 */
export function hideError() {
    const errorContainer = document.getElementById('error-recovery-container');
    if (errorContainer) {
        errorContainer.classList.add('hidden');
    }

    // Show appropriate content
    const filesSection = getElement('filesSection');
    const projectSelectionSection = getElement('projectSelectionSection');

    // Check if we have entity info to show files section
    const urlParams = new URLSearchParams(window.location.search);
    const hasEntityParams = urlParams.has('projectId') ||
                           urlParams.has('opportunityId') ||
                           urlParams.has('leadId') ||
                           urlParams.has('contactId');

    if (hasEntityParams && filesSection) {
        filesSection.classList.remove('hidden');
    } else if (projectSelectionSection) {
        projectSelectionSection.classList.remove('hidden');
    }
}

/**
 * Setup network connectivity monitoring
 * Automatically detects online/offline state and shows recovery options
 */
export function setupConnectivityMonitoring() {
    if (connectivityListenersAttached) {
        return; // Already set up
    }

    window.addEventListener('online', () => {
        logger.info('Connection restored');
        isOnline = true;

        // Hide offline errors
        const errorContainer = document.getElementById('error-recovery-container');
        if (errorContainer && errorContainer.textContent.includes('offline')) {
            hideError();

            // Show success message
            if (window.showSuccess) {
                window.showSuccess('Connection restored! You can continue working.');
                setTimeout(() => {
                    if (window.hideError) {
                        window.hideError();
                    }
                }, 3000);
            }
        }
    });

    window.addEventListener('offline', () => {
        logger.warn('Connection lost');
        isOnline = false;

        showErrorWithRecovery(
            new Error('No internet connection'),
            { type: ERROR_CONTEXTS.OFFLINE }
        );
    });

    connectivityListenersAttached = true;
    logger.info('Connectivity monitoring initialized');
}

/**
 * Check if browser is online
 * @returns {boolean}
 */
export function checkOnlineStatus() {
    return navigator.onLine && isOnline;
}

/**
 * Detect error type from error object
 * @param {Error} error - Error object
 * @param {number} [statusCode] - HTTP status code if available
 * @returns {string} ERROR_CONTEXTS type
 */
export function detectErrorType(error, statusCode) {
    // Check for offline
    if (!navigator.onLine) {
        return ERROR_CONTEXTS.OFFLINE;
    }

    // Check for network errors
    if (error.message && (
        error.message.includes('network') ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('NetworkError')
    )) {
        return ERROR_CONTEXTS.NETWORK_ERROR;
    }

    // Check for authentication errors
    if (statusCode === 401 || statusCode === 403) {
        return statusCode === 401
            ? ERROR_CONTEXTS.AUTHENTICATION
            : ERROR_CONTEXTS.PERMISSION_DENIED;
    }

    // Check for not found
    if (statusCode === 404) {
        return ERROR_CONTEXTS.ENTITY_NOT_FOUND;
    }

    // Check for API failures
    if (statusCode && statusCode >= 400) {
        return ERROR_CONTEXTS.API_FAILURE;
    }

    return ERROR_CONTEXTS.UNKNOWN;
}

/**
 * Helper function to get entity label
 * @private
 */
function getEntityLabel(entityType) {
    if (!entityType) return 'Entity';
    const config = ENTITY_TYPES[entityType];
    return config ? config.label : 'Entity';
}

/**
 * Escape HTML to prevent XSS
 * @private
 */
function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Initialize error recovery system
 * Should be called once when app loads
 */
export function initErrorRecovery() {
    setupConnectivityMonitoring();
    logger.info('Error recovery system initialized');
}

export default {
    showErrorWithRecovery,
    hideError,
    setupConnectivityMonitoring,
    checkOnlineStatus,
    detectErrorType,
    initErrorRecovery,
    ERROR_CONTEXTS
};
